import React, { useState, useEffect } from "react";
import axios from "axios";
import { APIURL } from "../../Global";
import { useAlert } from "react-alert";
import "./nurseroster.css";
import AddRoster from "./AddRoster";

const ManageRoster = () => {
  const [dates, setDates] = useState([]);
  const [rosterData, setRosterData] = useState([]);
  const [processedRoster, setProcessedRoster] = useState({});
  const [shiftNames, setShiftNames] = useState([]);
  const [show, setShow] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]); // Default to today
  const alert = useAlert();

  const fetchRoster = async (fromDate) => {
    const endDate = new Date(fromDate);
    endDate.setDate(endDate.getDate() + 28); // End date is 28 days from start date
    const formattedEndDate = endDate.toISOString().split("T")[0];

    try {
      const tokenString = sessionStorage.getItem("usertoken");
      const v2 = tokenString?.replace(/["]+/g, "");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Token ${v2}`,
      };
      const response = await axios.get(
        `${APIURL}/api/v1/service-provider/nurse-roster/?start_date=${fromDate}&end_date=${formattedEndDate}`,
        { headers }
      );
      if (response.data && Array.isArray(response.data.details)) {
        const assignedData = response.data.details.filter(
          (item) => item.status === "Assigned" // Filter only "Assigned" status
        );
        setRosterData(assignedData);

        // Extract unique shift types dynamically
        const shifts = [
          ...new Set(assignedData.map((item) => item.shift_type)),
        ];
        setShiftNames(shifts);

        // Generate the date range
        const datesArray = [];
        let currentDate = new Date(fromDate);
        while (currentDate <= endDate) {
          datesArray.push(currentDate.toISOString().split("T")[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }
        setDates(datesArray);
      }
    } catch (error) {
      console.error("Error fetching posted roster:", error);
      alert.error("Failed to fetch roster data.");
    }
  };

  useEffect(() => {
    fetchRoster(startDate);
  }, [startDate]);

  useEffect(() => {
    if (rosterData.length === 0 || dates.length === 0 || shiftNames.length === 0) return;

    // Process roster data into a structured format
    const processed = rosterData.reduce((acc, entry) => {
      const date = entry.date.split("T")[0]; // Extract YYYY-MM-DD
      const shift = entry.shift_type;

      if (!acc[date]) acc[date] = {}; // Initialize date group
      if (!acc[date][shift]) acc[date][shift] = []; // Initialize shift group for the date

      acc[date][shift].push(entry.nurse_name); // Add nurse to the corresponding shift
      return acc;
    }, {});

    // Ensure all dates and shifts are included, even if empty
    const completeRoster = dates.reduce((acc, date) => {
      if (!acc[date]) acc[date] = {}; // Initialize if date is missing

      shiftNames.forEach((shift) => {
        if (!acc[date][shift]) acc[date][shift] = []; // Initialize if shift is missing
      });

      return acc;
    }, processed);

    setProcessedRoster(completeRoster);
  }, [rosterData, dates, shiftNames]);

  const handleDateChange = (event) => {
    const newStartDate = event.target.value;
    setStartDate(newStartDate); // Update the start date
  };

  const onDragStart = (event, nurseName, date, fromShift) => {
    event.dataTransfer.setData(
      "draggedData",
      JSON.stringify({ nurseName, date, fromShift })
    );
  };

  const onDrop = async (event, toShift, date) => {
    event.preventDefault();
    const draggedData = JSON.parse(event.dataTransfer.getData("draggedData"));
    const { nurseName, fromShift } = draggedData;

    // Find the dragged roster item from the fetched roster data
    const rosterItem = rosterData.find(
      (item) =>
        item.nurse_name === nurseName &&
        item.date.split("T")[0] === date &&
        item.shift_type === fromShift
    );

    if (!rosterItem) {
      alert.error("Unable to find the selected roster item.");
      return;
    }

    // Find the shift details for the new shift type (toShift) on the same date
    let targetShift = rosterData.find(
      (item) =>
        item.shift_type.toLowerCase() === toShift.toLowerCase() &&
        item.date.split("T")[0] === date
    );

    // If no shift is found for the given date, look for any shift of the same type on another date
    if (!targetShift) {
      targetShift = rosterData.find(
        (item) => item.shift_type.toLowerCase() === toShift.toLowerCase()
      );
    }

    if (!targetShift) {
      alert.error(`No available shift details found for ${toShift} on any date.`);
      return;
    }

    // API URL and headers for the PUT request
    const apiUrl = `${APIURL}/api/v1/service-provider/nurse-roster/`;
    const tokenString = sessionStorage.getItem("usertoken");
    const v2 = tokenString?.replace(/["]+/g, "");
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${v2}`,
    };

    try {
      // Make the PUT request to update the nurse's shift
      const response = await axios.put(
        `${apiUrl}`,
        {
          roster_id: rosterItem.id,
          nurse_id: rosterItem.nurse,
          shift_type: toShift,
          status: targetShift.status || rosterItem.status,
          shift_start: targetShift.shift_start,
          shift_end: targetShift.shift_end,
          date: rosterItem.date.split("T")[0], // Keep the same date
        },
        { headers }
      );

      if (response.data.status === "success") {
        alert.success(`Moved ${nurseName} to ${toShift}`);
        fetchRoster(startDate); // Refresh data
      } else {
        throw new Error("Failed to update the shift");
      }
    } catch (error) {
      console.error("Error updating nurse shift:", error);
      alert.error(`Failed to move ${nurseName} to ${toShift}`);
    }
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "0 auto" }}>
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between" }}>
        <div>
          <label style={{ marginRight: "10px", fontWeight: "bold" }}>
            Start Date:
          </label>
          <input
            type="date"
            value={startDate}
            onChange={handleDateChange}
            style={{
              padding: "5px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              fontSize: "16px",
            }}
          />
        </div>
        <div><button className="roster-btn" onClick={() => { setShow(true) }}>Add Roster</button></div>
      </div>

      {Object.keys(processedRoster).length > 0 ? (
        <table className="roster-table" style={styles.table}>
          <thead style={{ textTransform: "capitalize" }}>
            <tr>
              <th style={styles.headerCell}>Date</th>
              {shiftNames.map((shift, idx) => (
                <th key={idx} style={styles.headerCell}>
                  {shift} Shift
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((date) => (
              <tr key={date}>
                <td style={styles.cell}>{date}</td>
                {shiftNames.map((shift, shiftIdx) => {
                  const nursesInShift = processedRoster[date]?.[shift] || [];

                  return (
                    <td
                      key={shiftIdx}
                      style={styles.cell}
                      onDragOver={onDragOver}
                      onDrop={(event) => onDrop(event, shift, date)}
                    >
                      {nursesInShift.map((nurseName, idx) => (
                        <div
                          key={idx}
                          draggable
                          onDragStart={(event) =>
                            onDragStart(event, nurseName, date, shift)
                          }
                          style={{
                            padding: "5px",
                            margin: "5px 0",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "5px",
                            cursor: "grab",
                            textTransform: "capitalize"
                          }}
                        >
                          {nurseName}
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <label style={{ color: "red", margin: "auto", fontSize: "25px" }}>
          No roster available!!
        </label>
      )}
      {show && <AddRoster show={show} onHide={() => setShow(false)} />}
    </div>
  );
};

const styles = {
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  headerCell: {
    border: "1px solid #ccc",
    padding: "8px",
    backgroundColor: "#f5f5f5",
    textAlign: "center",
    fontWeight: "bold",
  },
  cell: {
    border: "1px solid #ccc",
    padding: "8px",
    textAlign: "center",
    minHeight: "50px",
  },
};

export default ManageRoster;
